import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_ORIGIN = 'https://iiypv.com';

/**
 * Lightweight per-route SEO updater. No external deps (react-helmet
 * pulls in a chunky tree). On mount + route change we:
 *   - update document.title (keep 50-60 chars including brand suffix)
 *   - update <meta name="description"> (keep 120-160 chars)
 *   - update <meta name="keywords"> (optional, per page)
 *   - update <link rel="canonical"> to the current production URL
 *   - update Open Graph mirrors (og:title, og:description, og:url)
 *   - inject per-route JSON-LD structured data (GEO/AI + rich results)
 *
 * Pass `title`, `description`, `keywords`, `jsonLd` props per page.
 * `pathOverride` lets a page pin its canonical to a different URL.
 */
export default function SEO({ title, description, keywords, jsonLd, pathOverride, image, video, noindex }) {
  const location = useLocation();

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const fullTitle = title || 'IIY Phytoventra | BCM-95® Curcumin Supplements UK';
    const fullDesc = description ||
      'IIY Phytoventra (Invest In Yourself) — BCM-95® curcumin food supplements, UK MHRA/ASA-compliant. 100% Curcugreen, vegan, allergen-free.';
    const path = pathOverride || location.pathname || '/';
    const canonical = `${SITE_ORIGIN}${path === '/' ? '/' : path.replace(/\/$/, '')}`;
    const ogImage = image || `${SITE_ORIGIN}/og-image.jpg`;

    document.title = fullTitle;
    setMeta('name', 'description', fullDesc);
    setLink('canonical', canonical);
    setMeta('property', 'og:title', fullTitle);
    setMeta('property', 'og:description', fullDesc);
    setMeta('property', 'og:url', canonical);
    setMeta('property', 'og:image', ogImage);
    setMeta('name', 'twitter:title', fullTitle);
    setMeta('name', 'twitter:description', fullDesc);
    setMeta('name', 'twitter:image', ogImage);
    // Robots: keep utility/auth pages out of the index to avoid duplicate
    // title/description flags; reset to index,follow on every other route.
    setMeta('name', 'robots', noindex ? 'noindex, follow' : 'index, follow');

    // Video share card (og:video + twitter player). Reset to defaults otherwise
    // so these tags never leak onto non-video routes during SPA navigation.
    if (video && video.url) {
      setMeta('property', 'og:type', 'video.other');
      setMeta('property', 'og:video', video.url);
      setMeta('property', 'og:video:secure_url', video.url);
      setMeta('property', 'og:video:type', video.type || 'text/html');
      setMeta('property', 'og:video:width', String(video.width || 1280));
      setMeta('property', 'og:video:height', String(video.height || 720));
      setMeta('name', 'twitter:card', 'player');
      setMeta('name', 'twitter:player', video.url);
      setMeta('name', 'twitter:player:width', String(video.width || 1280));
      setMeta('name', 'twitter:player:height', String(video.height || 720));
    } else {
      setMeta('property', 'og:type', 'website');
      setMeta('name', 'twitter:card', 'summary_large_image');
    }

    if (keywords) setMeta('name', 'keywords', keywords);

    setJsonLd(jsonLd);
  }, [title, description, keywords, jsonLd, pathOverride, location.pathname, image, video, noindex]);

  return null;
}

function setMeta(attr, key, value) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
}

function setLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

// Injects (or removes) a single per-route JSON-LD block. Kept separate
// from the static <script type="application/ld+json"> in index.html so
// crawlers still get the sitewide Organization/Product graph on first byte.
function setJsonLd(data) {
  const ID = 'route-jsonld';
  let el = document.getElementById(ID);
  if (!data) {
    if (el) el.remove();
    return;
  }
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = ID;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}
